import React, { ReactNode } from "react";
import { Alert, Button, Col, Container, Form, Row } from "react-bootstrap";
import { BookHalf, Funnel, FunnelFill, Sun, Moon } from "react-bootstrap-icons";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";

import { getAnalyticsWidget, getGenres, createExportSchedule } from "../../../utils/Requests";
import { getAnalyticsBrandLabel, getDefaultDateRange, getWidgetTheme, replaceAnalyticsBrandTerminology } from "../../../utils/analytics";
import { AnalyticsFilters, AnalyticsFilterOptions, DocsSidebarEntry } from "../../../types/analytics";
import AnalyticsFiltersComponent from "../../../components/analytics/AnalyticsFilters";
import MetricCards, { MetricCardItem } from "../../../components/analytics/MetricCards";
import { DocsSidebar, ExportButton, ScheduleExportModal, WidgetCard, PopPeriodBanner } from "../../../components/analytics";
import { ExportContext, ExportableData, ExportScheduleRequest } from "../../../services/export";
import Logger from "../../../utils/Logger";
import { truncateAxisLabelToWidth } from "../widgets/utils";

type ContentGenreExplorerProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type ContentGenreExplorerState = {
    isGenresLoading: boolean;
    isLoading: boolean;
    error: string | null;
    widgetStates: WidgetStateMap;
    filters: AnalyticsFilters;
    appliedFilters: AnalyticsFilters;
    filterOptions: AnalyticsFilterOptions;
    filtersCollapsed: boolean;
    isDarkTheme: boolean;
    genres: string[];
    selectedGenre: string;
    metricsData: Record<string, any>[];
    topBrandsData: TopBrandPoint[];
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
    showScheduleModal: boolean;
    editingSchedule: null;
};

type TopBrandPoint = {
    thingBrandName: string;
    plays: number;
};

type WidgetLoadState = {
    isLoading: boolean;
    error: string | null;
};

type WidgetStateMap = Record<string, WidgetLoadState>;

const CONTENT_GENRE_WIDGET_KEYS = [
    "content_genre_explorer_metrics",
    "content_genre_explorer_top_brands"
];

const createInitialWidgetStates = (isLoading: boolean): WidgetStateMap => {
    return CONTENT_GENRE_WIDGET_KEYS.reduce<WidgetStateMap>((acc, widgetKey) => {
        acc[widgetKey] = { isLoading, error: null };
        return acc;
    }, {});
};

class ContentGenreExplorer extends React.Component<ContentGenreExplorerProps, ContentGenreExplorerState> {
    private static readonly TOP_BRANDS_AXIS_WIDTH = 280;
    private static readonly TOP_BRANDS_AXIS_TEXT_MAX_WIDTH = 236;
    private activeRequestId = 0;
    private isMountedFlag = false;

    constructor(props: ContentGenreExplorerProps) {
        super(props);

        const { startDate, endDate } = getDefaultDateRange();
        const initialFilters: AnalyticsFilters = {
            startDate,
            endDate,
            attributionId: [],
            brandName: [],
            devicePlatform: []
        };

        this.state = {
            isGenresLoading: true,
            isLoading: false,
            error: null,
            widgetStates: createInitialWidgetStates(false),
            filters: initialFilters,
            appliedFilters: initialFilters,
            filterOptions: {
                attributionIds: [],
                brandNames: [],
                devicePlatforms: []
            },
            filtersCollapsed: false,
            isDarkTheme: true,
            genres: [],
            selectedGenre: "",
            metricsData: [],
            topBrandsData: [],
            sidebarOpen: false,
            sidebarEntries: [],
            showScheduleModal: false,
            editingSchedule: null
        };
    }

    componentDidMount(): void {
        this.isMountedFlag = true;
        this.initializePage();
    }

    componentWillUnmount(): void {
        this.isMountedFlag = false;
        this.activeRequestId += 1;
    }

    private toggleTheme = (): void => {
        this.setState(prevState => ({ isDarkTheme: !prevState.isDarkTheme }));
    };

    private initializePage = async (): Promise<void> => {
        try {
            this.setState({ isGenresLoading: true, error: null });

            const genreResponse = await getGenres(this.props.customerConfig);
            const genreBody = await genreResponse.json();
            const rawGenres: unknown = genreBody?.items?.[0]?.genres;
            const genreOptions = Array.isArray(rawGenres)
                ? rawGenres
                    .filter((genre): genre is string => typeof genre === "string")
                : [];

            const normalizedGenres = [...genreOptions];
            if (!normalizedGenres.includes("All Genres")) {
                normalizedGenres.unshift("All Genres");
            }

            const defaultGenre = "All Genres";

            this.setState({
                genres: normalizedGenres,
                selectedGenre: defaultGenre,
                isGenresLoading: false
            }, () => {
                if (defaultGenre) {
                    this.fetchExplorerData(defaultGenre);
                }
            });
        } catch (error) {
            Logger.error("Error initializing content genre explorer", error);
            this.setState({
                error: "Failed to load content genre explorer",
                isGenresLoading: false
            });
        }
    };

    private handleFilterChange = (filters: AnalyticsFilters): void => {
        this.setState({ filters });
    };

    private toggleSidebar = (): void => {
        this.setState(prevState => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private getExportData = (): ExportableData[] => {
        const { metricsData, topBrandsData, selectedGenre, appliedFilters } = this.state;
        const data: ExportableData[] = [];

        if (metricsData.length > 0) {
            const row = metricsData[0];
            const metricRows = Object.keys(row)
                .filter(k => !k.endsWith("_percent_change"))
                .map(k => ({ metric: k, value: row[k] }));
            data.push({
                title: `Genre Metrics - ${selectedGenre}`,
                columns: [
                    { key: "metric", label: "Metric" },
                    { key: "value", label: "Value", format: "number" }
                ],
                rows: metricRows,
                metadata: { filters: appliedFilters, pageKey: "content_genre_explorer", customerSlug: this.props.customerConfig.slug }
            });
        }

        if (topBrandsData.length > 0) {
            data.push({
                title: `${replaceAnalyticsBrandTerminology("Top Brands", this.props.customerConfig)} - ${selectedGenre}`,
                columns: [
                    { key: "thingBrandName", label: getAnalyticsBrandLabel(this.props.customerConfig) },
                    { key: "plays", label: "Plays", format: "number" }
                ],
                rows: topBrandsData,
                metadata: { filters: appliedFilters, pageKey: "content_genre_explorer", customerSlug: this.props.customerConfig.slug }
            });
        }

        return data;
    };

    private getExportContext = (): ExportContext => {
        return {
            pageTitle: "Genre Explorer",
            pageKey: "content_genre_explorer",
            customerSlug: this.props.customerConfig.slug,
            filters: this.state.appliedFilters,
            getData: this.getExportData,
            getContainerRef: () => null
        };
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
    };

    private buildSidebarEntry = (widgetKey: string, response: unknown): DocsSidebarEntry | null => {
        const meta = (response as any)?.widget;
        if (!meta) {
            return null;
        }

        return {
            widgetKey,
            displayName: meta.display_name,
            description: meta.description,
            schema: (response as any)?.schema
        };
    };

    private upsertSidebarEntry = (entries: DocsSidebarEntry[], entry: DocsSidebarEntry): DocsSidebarEntry[] => {
        const nextEntries = entries.filter(current => current.widgetKey !== entry.widgetKey);
        nextEntries.push(entry);
        return nextEntries;
    };

    private updateWidget = async (
        requestId: number,
        widgetKey: string,
        loadResponse: () => Promise<unknown>,
        applyResponse: (response: unknown) => Partial<ContentGenreExplorerState>
    ): Promise<void> => {
        try {
            const response = await loadResponse();

            if (!this.isMountedFlag || requestId !== this.activeRequestId) {
                return;
            }

            const sidebarEntry = this.buildSidebarEntry(widgetKey, response);

            this.setState(prevState => {
                const widgetStates = {
                    ...prevState.widgetStates,
                    [widgetKey]: { isLoading: false, error: null }
                } as WidgetStateMap;

                return {
                    ...applyResponse(response),
                    sidebarEntries: sidebarEntry ? this.upsertSidebarEntry(prevState.sidebarEntries, sidebarEntry) : prevState.sidebarEntries,
                    widgetStates,
                    isLoading: Object.values(widgetStates).some((state: WidgetLoadState) => state.isLoading)
                } as ContentGenreExplorerState;
            });
        } catch (error) {
            if (!this.isMountedFlag || requestId !== this.activeRequestId) {
                return;
            }

            Logger.error(`Error fetching ${widgetKey}`, error);
            this.setState(prevState => {
                const widgetStates = {
                    ...prevState.widgetStates,
                    [widgetKey]: { isLoading: false, error: "Failed to load widget data" }
                } as WidgetStateMap;

                return {
                    widgetStates,
                    isLoading: Object.values(widgetStates).some((state: WidgetLoadState) => state.isLoading)
                } as ContentGenreExplorerState;
            });
        }
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters }
        }), () => {
            const { selectedGenre } = this.state;
            if (selectedGenre) {
                this.fetchExplorerData(selectedGenre);
            }
        });
    };

    private handleGenreChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedGenre = event.target.value;
        this.setState({ selectedGenre }, () => {
            if (selectedGenre) {
                this.fetchExplorerData(selectedGenre);
            } else {
                this.setState({ metricsData: [], topBrandsData: [] });
            }
        });
    };

    private fetchExplorerData = async (selectedGenre: string): Promise<void> => {
        const { appliedFilters } = this.state;
        const requestId = ++this.activeRequestId;

        this.setState({ isLoading: true, error: null, widgetStates: createInitialWidgetStates(true), metricsData: [], topBrandsData: [], sidebarEntries: [] });

        void this.updateWidget(requestId, "content_genre_explorer_metrics", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_genre_explorer_metrics",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform,
            undefined,
            undefined,
            selectedGenre === "All Genres" ? undefined : { genre: selectedGenre }
        ), (response: unknown) => ({
            metricsData: Array.isArray((response as any)?.result) ? (response as any).result : []
        }));

        void this.updateWidget(requestId, "content_genre_explorer_top_brands", () => getAnalyticsWidget(
            this.props.customerConfig,
            "content_genre_explorer_top_brands",
            appliedFilters.startDate,
            appliedFilters.endDate,
            appliedFilters.attributionId,
            appliedFilters.brandName,
            appliedFilters.devicePlatform,
            undefined,
            undefined,
            selectedGenre === "All Genres" ? undefined : { genre: selectedGenre }
        ), (response: unknown) => {
            const topBrandsRaw = (response as any)?.result;
            return {
                topBrandsData: (Array.isArray(topBrandsRaw) ? topBrandsRaw : [])
                    .map((row: Record<string, any>) => ({
                        thingBrandName: String(row?.thing_brand_name || row?.thing_brandname || "Unknown"),
                        plays: this.toNumberOrNull(row?.plays) ?? 0
                    }))
                    .sort((a: TopBrandPoint, b: TopBrandPoint) => b.plays - a.plays)
                    .slice(0, 25)
            };
        });
    };

    private toTitleCase = (value: string): string => {
        return value
            .replace(/_/g, " ")
            .replace(/\b\w/g, char => char.toUpperCase());
    };

    private toNumberOrNull = (value: unknown): number | null => {
        if (typeof value === "number" && Number.isFinite(value)) {
            return value;
        }
        if (typeof value === "string") {
            const parsed = Number(value);
            if (Number.isFinite(parsed)) {
                return parsed;
            }
        }
        return null;
    };

    private getTopBrandsAxisFont = (): string => {
        if (typeof document === "undefined") {
            return "14px sans-serif";
        }

        const bodyStyle = window.getComputedStyle(document.body);
        return bodyStyle.font || `${bodyStyle.fontSize || "14px"} ${bodyStyle.fontFamily || "sans-serif"}`;
    };

    private renderTopBrandsAxisTick = (tickProps: any): ReactNode => {
        const { x, y, payload } = tickProps;
        const fullLabel = String(payload?.value ?? "");
        const axisFont = this.getTopBrandsAxisFont();
        const truncatedLabel = truncateAxisLabelToWidth(
            fullLabel,
            ContentGenreExplorer.TOP_BRANDS_AXIS_TEXT_MAX_WIDTH,
            axisFont
        );

        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dominantBaseline="middle" textAnchor="end" fill="#ffffff" style={{ font: axisFont }}>
                    <title>{fullLabel}</title>
                    {truncatedLabel}
                </text>
            </g>
        );
    };

    private getMetricCards = (): MetricCardItem[] => {
        const { metricsData, sidebarEntries } = this.state;
        if (!Array.isArray(metricsData) || metricsData.length === 0) {
            return [];
        }

        const schema = sidebarEntries.find(e => e.widgetKey === "content_genre_explorer_metrics")?.schema as Record<string, { description?: string }> | undefined;
        const sourceRow = metricsData[0] as Record<string, any>;
        const ignoredKeys = new Set([
            "genre",
            "genre_name",
            "genreName",
            "date",
            "datepartition",
            "brand_count"
        ]);

        const candidateKeys = Object.keys(sourceRow)
            .filter(key => !ignoredKeys.has(key))
            .filter(key => !key.startsWith("previous_"))
            .filter(key => !key.endsWith("_diff"))
            .filter(key => !key.endsWith("_pop_Pct"))
            .filter(key => !key.endsWith("_pop_pct"))
            .filter(key => !key.endsWith("_percent_change"))
            .filter(key => !key.endsWith("_change"))
            .filter(key => this.toNumberOrNull(sourceRow[key]) !== null)
            .slice(0, 8);

        return candidateKeys.map((key): MetricCardItem => {
            const value = this.toNumberOrNull(sourceRow[key]);
            const percentChange = this.toNumberOrNull(sourceRow[`${key}_pop_Pct`])
                ?? this.toNumberOrNull(sourceRow[`${key}_pop_pct`])
                ?? this.toNumberOrNull(sourceRow[`${key}_percent_change`])
                ?? this.toNumberOrNull(sourceRow[`percent_change_${key}`]);
            const explicitNumericChange = this.toNumberOrNull(sourceRow[`${key}_diff`])
                ?? this.toNumberOrNull(sourceRow[`${key}_change`]);
            const previousValue = this.toNumberOrNull(sourceRow[`previous_${key}`]);
            const numericChange = explicitNumericChange
                ?? (value !== null && previousValue !== null ? value - previousValue : null);

            const isPercentMetric = key.includes("percent") || key.includes("rate");
            const isDecimalMetric = key.includes("per_") && !isPercentMetric;

            return {
                key,
                label: this.toTitleCase(key),
                value,
                changePercent: percentChange,
                numericChange,
                valueFormat: isPercentMetric ? "percent" : isDecimalMetric ? "decimal" : "number",
                description: schema?.[key]?.description
            };
        });
    };

    private getBrandBarColor = (index: number, total: number): string => {
        if (total <= 1) {
            return "#007bff";
        }

        const startHue = 205; // blue
        const endHue = 285; // purple
        const hue = startHue + ((endHue - startHue) * index) / (total - 1);

        return `hsl(${hue}, 78%, 52%)`;
    };

    public render(): ReactNode {
        const { filters, filterOptions, filtersCollapsed, appliedFilters, genres, selectedGenre, isGenresLoading, isLoading, error, topBrandsData, isDarkTheme, sidebarOpen, sidebarEntries, widgetStates } = this.state;
        const metricCards = this.getMetricCards();
        const theme = getWidgetTheme(isDarkTheme);
        const allWidgetsSettled = Object.values(widgetStates).every(state => !state.isLoading);

        if (isGenresLoading) {
            return (
                <Container className="mw-100" key="genre-explorer" style={{ paddingBottom: "40px" }}>
                    <Row>
                        <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                            <h2>Genre Explorer</h2>
                        </Col>
                    </Row>
                </Container>
            );
        }

        return (
            <div style={{ display: "flex", alignItems: "flex-start", backgroundColor: isDarkTheme ? "#060606" : "#ffffff", color: isDarkTheme ? "#ffffff" : "#000000", minHeight: "100vh" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
            <Container className="mw-100" key="genre-explorer" style={{ position: "relative", paddingBottom: "40px" }}>
                <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                    <ExportButton
                        context={this.getExportContext()}
                        enabled={allWidgetsSettled && !this.state.error && this.state.metricsData.length > 0}
                        scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                        onScheduleClick={() => this.setState({ showScheduleModal: true })}
                    />
                    <Button
                        onClick={this.toggleTheme}
                        style={{ backgroundColor: "transparent", border: "none", fontSize: "24px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                        title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
                    >
                        {isDarkTheme ? <Sun /> : <Moon />}
                    </Button>
                    <Button
                        onClick={() => this.setState(prevState => ({ filtersCollapsed: !prevState.filtersCollapsed }))}
                        style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                        title={filtersCollapsed ? "Show filters" : "Hide filters"}
                    >
                        {filtersCollapsed ? <Funnel /> : <FunnelFill />}
                    </Button>
                    <Button
                        onClick={this.toggleSidebar}
                        style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                        title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                    >
                        <BookHalf />
                    </Button>
                </div>
                <Row>
                    <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                        <h2 style={{ color: theme.text }}>Genre Explorer</h2>
                    </Col>
                </Row>

                <AnalyticsFiltersComponent
                    filters={filters}
                    filterOptions={filterOptions}
                    onFilterChange={this.handleFilterChange}
                    onApplyFilters={this.handleApplyFilters}
                    customerConfig={this.props.customerConfig}
                    enableGroupBy={{}}
                    isCollapsed={filtersCollapsed}
                />
                <PopPeriodBanner startDate={appliedFilters.startDate} endDate={appliedFilters.endDate} />

                <Row style={{ marginTop: "8px", marginBottom: "20px" }}>
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Genre</Form.Label>
                            <Form.Control
                                as="select"
                                value={selectedGenre}
                                onChange={this.handleGenreChange}
                                disabled={genres.length === 0 || isLoading || isGenresLoading}
                            >
                                {genres.length === 0 ? (
                                    <option value="">No genres available</option>
                                ) : (
                                    genres.map(genre => (
                                        <option key={genre} value={genre}>{genre}</option>
                                    ))
                                )}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                </Row>

                {error && (
                    <Row>
                        <Col>
                            <Alert variant="danger">{error}</Alert>
                        </Col>
                    </Row>
                )}

                <Row>
                    <Col>
                        <WidgetCard
                            title="Genre Metrics"
                            description={sidebarEntries.find(e => e.widgetKey === "content_genre_explorer_metrics")?.description}
                            theme={theme}
                            isLoading={widgetStates.content_genre_explorer_metrics.isLoading}
                            error={widgetStates.content_genre_explorer_metrics.error}
                            isEmpty={!widgetStates.content_genre_explorer_metrics.isLoading && !widgetStates.content_genre_explorer_metrics.error && metricCards.length === 0}
                            emptyMessage="No numeric metrics available to render as KPI widgets."
                        >
                            <MetricCards
                                metrics={metricCards}
                                theme={theme}
                                combineChangeAndDelta={true}
                                noChangeLabel="—"
                            />
                        </WidgetCard>
                    </Col>
                </Row>

                <Row style={{ marginTop: "20px" }}>
                    <Col>
                        <WidgetCard
                            title={replaceAnalyticsBrandTerminology("Top Brands by Plays", this.props.customerConfig)}
                            description={sidebarEntries.find(e => e.widgetKey === "content_genre_explorer_top_brands")?.description}
                            theme={theme}
                            isLoading={widgetStates.content_genre_explorer_top_brands.isLoading}
                            error={widgetStates.content_genre_explorer_top_brands.error}
                            isEmpty={!widgetStates.content_genre_explorer_top_brands.isLoading && !widgetStates.content_genre_explorer_top_brands.error && topBrandsData.length === 0}
                            emptyMessage={replaceAnalyticsBrandTerminology("No top brand data available for this genre and date range.", this.props.customerConfig)}
                        >
                            <ResponsiveContainer width="100%" height={Math.max(560, topBrandsData.length * 28)}>
                                <BarChart
                                    data={topBrandsData}
                                    layout="vertical"
                                    margin={{ top: 5, right: 24, left: 12, bottom: 5 }}
                                >
                                    <CartesianGrid stroke="#ffffff" strokeDasharray="3 3" />
                                    <XAxis
                                        type="number"
                                        tick={{ fill: "#ffffff" }}
                                        axisLine={{ stroke: "rgba(255,255,255,0.2)" }}
                                        tickLine={{ stroke: "rgba(255,255,255,0.2)" }}
                                        tickFormatter={(value: number) => value.toLocaleString()}
                                    />
                                    <YAxis
                                        type="category"
                                        dataKey="thingBrandName"
                                        width={280}
                                        interval={0}
                                        tick={{ fill: "#ffffff", fontSize: 12 }}
                                        axisLine={{ stroke: "rgba(255,255,255,0.2)" }}
                                        tickLine={{ stroke: "rgba(255,255,255,0.2)" }}
                                    />
                                    <Tooltip
                                        formatter={(value: number | string | undefined) => [Number(value ?? 0).toLocaleString(), "Plays"]}
                                        contentStyle={{
                                            backgroundColor: "#1f1f1f",
                                            border: "1px solid rgba(255,255,255,0.2)",
                                            color: "#ffffff"
                                        }}
                                        labelStyle={{ color: "#ffffff" }}
                                        itemStyle={{ color: "#ffffff" }}
                                    />
                                    <Bar dataKey="plays">
                                        {topBrandsData.map((entry: TopBrandPoint, index: number) => (
                                            <Cell
                                                key={`brand-bar-${entry.thingBrandName}-${index}`}
                                                fill={this.getBrandBarColor(index, topBrandsData.length)}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </WidgetCard>
                    </Col>
                </Row>
            </Container>
            </div>
            <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
            <ScheduleExportModal
                show={this.state.showScheduleModal}
                onHide={() => this.setState({ showScheduleModal: false })}
                filters={this.state.appliedFilters}
                pageKey="content_genre_explorer"
                pageTitle="Genre Explorer"
                customerSlug={this.props.customerConfig.slug}
                customerConfig={this.props.customerConfig}
                editingSchedule={this.state.editingSchedule}
                onSave={this.handleScheduleSave}
            />
            </div>
        );
    }
}

export default ContentGenreExplorer;